import { inject } from '@angular/core'
import { RedirectCommand, Router, UrlTree } from '@angular/router'
import { map, catchError, of } from 'rxjs'

import { CompanyContextService } from '@/app/services/http/company-context.service'
import { AuthenticationService } from '@/app/core/service/auth.service'

export const companyContextGuard = () => {
  const companyContext = inject(CompanyContextService)
  const authenticationService = inject(AuthenticationService)
  const router: Router = inject(Router)

  // Admin/support aren't staff of any company — bounce them to the company directory
  // instead of the client "My Companies" switcher.
  const toCompanies = (): UrlTree =>
    router.parseUrl(
      authenticationService.isAdmin || authenticationService.isSupport
        ? '/pages/admin/companies'
        : '/companies'
    )

  const activeCompanyId = companyContext.activeCompanyId
  if (!activeCompanyId) {
    return new RedirectCommand(toCompanies(), { skipLocationChange: true })
  }

  // A selected id alone isn't enough to trust — it may be stale (a leftover cookie
  // from a previous session/account on this browser) or simply wrong. Confirm it's
  // actually one of this user's own companies before letting the page load.
  return companyContext.getCompanies().pipe(
    map((companies) => {
      const stillOwned = companies.some((c) => c.id === activeCompanyId)
      if (stillOwned) return true

      companyContext.clearActiveCompany()
      return new RedirectCommand(toCompanies(), { skipLocationChange: true })
    }),
    catchError(() => {
      // Can't verify right now — fail closed rather than trust an unverified selection.
      companyContext.clearActiveCompany()
      return of(new RedirectCommand(toCompanies(), { skipLocationChange: true }))
    })
  )
}
